import { employeeSchema } from "@/components/Forms/schema/employeeSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "employee",
    attributesSchema: employeeSchema,
});

export const employeeSelector = createDataSelector(resourceSelector);
export type NewEmployee = ReturnType<typeof employeeSelector>;

type CaseTaskWithNote = {
    idUUID: string;
    caseUUID?: string;
    note: string;
    caseStatus: string;
    saveType?: string;
};

export const useCompleteTask = (): UseMutationResult<void, Error, CaseTaskWithNote> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attributes) => {
            const response = await fetch(apiUrl("/case-task/complete"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    ...attributes,
                    saveType: attributes.saveType ?? "complete",
                }),
            });

            await handleJsonApiError(response);
        },
        onSuccess: async (_data, { caseUUID }) => {
            await queryClient.invalidateQueries({
                queryKey: ["case-tasks"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["dashboard-tasks-table-data"],
            });

            if (caseUUID) {
                await queryClient.invalidateQueries({
                    queryKey: ["case-notes", caseUUID],
                });
            }
        },
    });
};
